import React, { forwardRef } from "react";
import Box from "@mui/material/Box";
import FormControl from "@mui/material/FormControl";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { Dayjs } from "dayjs";
import { Button, SxProps } from "@mui/material";
import { AppTheme } from "../../utils/theme";

interface BasicDatepickerProps {
	value?: Dayjs | null;
	label: string;
	onChange?: (value: Dayjs | null) => void;
	onClear?: () => void;
	sx?: SxProps<AppTheme> | undefined;
	disableFuture?: boolean;
	ianaTimeZone?: string;
	format?: string;
	// SIMS-8895 (2): range bounds and the error state for a caller that pairs two
	// pickers. All optional and unset by default, so existing callers keep exactly
	// the behaviour they have today.
	minDate?: Dayjs;
	maxDate?: Dayjs;
	error?: boolean;
}

const BasicDatepicker = forwardRef<HTMLDivElement, BasicDatepickerProps>(
	(
		{
			value,
			label,
			onChange,
			onClear,
			sx,
			disableFuture = true,
			ianaTimeZone,
			format,
			minDate,
			maxDate,
			error,
		},
		ref,
	) => {
		return (
			<LocalizationProvider dateAdapter={AdapterDayjs}>
				<Box
					ref={ref}
					sx={{
						minWidth: 180,
						display: "flex",
						flexGrow: 1,
						maxWidth: "100%",
						height: "3.5rem",
						...sx,
					}}
				>
					<FormControl fullWidth sx={{ height: "3.5rem" }}>
						<DatePicker
							timezone={ianaTimeZone}
							value={value}
							label={label}
							format={format}
							disableFuture={disableFuture ?? true}
							minDate={minDate}
							maxDate={maxDate}
							onChange={onChange}
							// The picker has no error prop of its own, so the flag has to reach
							// the underlying TextField. Undefined for callers that never set
							// it, which leaves MUI's default field untouched.
							slotProps={{ textField: { error } }}
						/>
					</FormControl>
					{onClear && (
						<Button onClick={onClear} sx={{ ml: 1, height: "3.5rem" }}>
							Clear
						</Button>
					)}
				</Box>
			</LocalizationProvider>
		);
	},
);

BasicDatepicker.displayName = "BasicDatepicker";

export default BasicDatepicker;
